import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api/client";
import { fetchBillingAccess } from "../lib/billingAccess";
import BillingNav from "../components/BillingNav";

// WC follow-up by carrier (billing PR #6, 2026-10-30) - SHADOW and DARK.
//   /billing/wc/followup                       open WC claims entered in the window, by carrier:
//                                              never paid, paid short, due (unpaid past 21 days e-billed / 45 paper), aging
//   /billing/wc/followup/carrier/:payorId      one carrier's open claims, due first, then oldest
//   /billing/wc/followup/no-carrier            the open claims with no carrier on file
// The window lives in the URL as ?window=90|365|730 - never a patient, case or claim number.
// Behind the server's billing gate like the rest of Billing. Reads only: nothing is sent to a carrier.

const PAGE = 100;
const WINDOWS = ["90", "365", "730"];

function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function usDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}

function readWindow(search) {
  const w = new URLSearchParams(search).get("window");
  return WINDOWS.includes(w) ? w : "365";
}

function windowSearch(win) {
  return win === "365" ? "" : `?window=${win}`;
}

function Note({ children, tone = "slate" }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600";
  return <div className={`mt-4 rounded-lg border p-4 text-sm ${cls}`}>{children}</div>;
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function WindowToggle({ win, base }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm" data-testid="wcf-window">
      {WINDOWS.map((w) => (
        <Link key={w} to={{ pathname: base, search: windowSearch(w) }}
              className={`rounded-md px-3 py-1 ${w === win ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          Entered in the last {w} days
        </Link>
      ))}
    </div>
  );
}

function Summary({ win }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    api
      .get("/billing/wc/followup", { params: { window: win } })
      .then((res) => live && setData(res.data))
      .catch(() => live && setError("Couldn't load the follow-up list."));
    return () => {
      live = false;
    };
  }, [win]);

  if (error) return <Note tone="amber">{error}</Note>;
  if (!data) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  const t = data.totals;
  return (
    <>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Open claims" value={t.claims.toLocaleString()} sub={`${money(t.balance)} still owed`} testid="wcf-open" />
        <Stat label={`Due (unpaid past ${data.due_days_ebill} days e-billed, ${data.due_days} paper)`} value={t.due.toLocaleString()} sub={money(t.due_balance)}
              testid="wcf-due" />
        <Stat label="Never paid" value={t.never_paid.toLocaleString()} />
        <Stat label="Paid short" value={t.paid_short.toLocaleString()} sub="some payment, balance left" />
      </div>
      <div className="mt-3 grid grid-cols-5 gap-2 text-center text-xs" data-testid="wcf-buckets">
        {t.buckets.map((b) => (
          <div key={b.bucket} className="rounded-lg border border-slate-200 bg-white p-2">
            <div className="text-slate-500">{b.bucket} days</div>
            <div className="text-sm font-semibold text-slate-800">{b.claims.toLocaleString()}</div>
            <div className="text-slate-500">{money(b.balance)}</div>
          </div>
        ))}
      </div>

      <h2 className="mt-6 text-base font-semibold text-slate-800">By carrier ({data.carriers.length})</h2>
      <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="wcf-carriers">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Carrier</th>
              <th className="px-3 py-2 text-right">Open claims</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2 text-right">Due</th>
              <th className="px-3 py-2 text-right">Due balance</th>
              <th className="px-3 py-2 text-right">Never paid</th>
              <th className="px-3 py-2 text-right">Paid short</th>
              <th className="px-3 py-2 text-right">Oldest (days)</th>
              <th className="px-3 py-2">Last payment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.carriers.map((c) => (
              <tr key={c.payor_id === null ? "none" : c.payor_id} className="hover:bg-slate-50">
                <td className="px-3 py-1.5">
                  {c.payor_id === null ? (
                    <Link to={{ pathname: "/billing/wc/followup/no-carrier", search: windowSearch(win) }}
                          className="text-blue-600 hover:underline">{c.name}</Link>
                  ) : (
                    <Link to={{ pathname: `/billing/wc/followup/carrier/${c.payor_id}`, search: windowSearch(win) }}
                          className="text-blue-600 hover:underline">{c.name || `Carrier ${c.payor_id}`}</Link>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right">{c.claims.toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right">{money(c.balance)}</td>
                <td className={`px-3 py-1.5 text-right ${c.due > 0 ? "font-medium text-rose-700" : ""}`}>{c.due.toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right">{money(c.due_balance)}</td>
                <td className="px-3 py-1.5 text-right">{c.never_paid.toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right">{c.paid_short.toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right">{c.oldest_days ?? "—"}</td>
                <td className="px-3 py-1.5">{usDate(c.last_payment)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-500">
        A claim = one WC case + one date of service, with a Raintree balance above $0, entered (first posted in Raintree)
        in the window. Due = never paid and entered more than {data.due_days_ebill} days ago when the carrier takes
        electronic bills (California gives the carrier 15 working days from receiving an electronic bill, Labor Code
        4603.4), else more than {data.due_days} days ago for a paper bill (45 days, 60 for a government employer, Labor
        Code 4603.2(b)(2)); the clock here starts at entry. Which carriers WestStar e-bills is still to be confirmed.
        {t.past_lien_limit > 0 && <> {t.past_lien_limit.toLocaleString()} open claims ({money(t.past_lien_limit_balance)})
        have a date of service more than {data.lien_limit_months} months ago - past the lien filing limit (Labor Code
        4903.5) if they ever go the lien route.</>} Out-of-state carriers follow their own state's rules.
      </p>
    </>
  );
}

function CarrierClaims({ payorId, win }) {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setOffset(0);
  }, [payorId, win]);

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    const url = payorId ? `/billing/wc/followup/carriers/${payorId}` : "/billing/wc/followup/no-carrier";
    api
      .get(url, { params: { window: win, offset, limit: PAGE } })
      .then((res) => live && setData(res.data))
      .catch((e) => live && setError(e?.response?.status === 404 ? "There's no such carrier." : "Couldn't load these claims."));
    return () => {
      live = false;
    };
  }, [payorId, win, offset]);

  if (error) return <Note tone="amber">{error}</Note>;
  if (!data) return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  const k = data.carrier;
  return (
    <>
      <h2 className="mt-4 text-lg font-semibold text-slate-900" data-testid="wcf-carrier-name">{data.name}</h2>
      {k && (
        <p className="text-sm text-slate-500">
          {[k.phone && `Phone ${k.phone}`, k.fax && `Fax ${k.fax}`, [k.city, k.state].filter(Boolean).join(", "),
            k.electronic_payer_id ? `Payer ID ${k.electronic_payer_id}` : "No electronic payer ID (paper)"].filter(Boolean).join(" · ")}
        </p>
      )}
      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm" data-testid="wcf-claims">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Patient</th>
              <th className="px-3 py-2">Case</th>
              <th className="px-3 py-2">Claim #</th>
              <th className="px-3 py-2">Date of service</th>
              <th className="px-3 py-2">Entered</th>
              <th className="px-3 py-2 text-right">Days</th>
              <th className="px-3 py-2 text-right">Billed</th>
              <th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2">Last payment</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.claims.map((c) => (
              <tr key={`${c.case_id}-${c.date_of_service}`}>
                <td className="px-3 py-1.5">
                  <Link to={`/billing/case/${c.case_id}`} className="font-medium text-blue-700 hover:underline">
                    {c.patient_name || "—"}
                  </Link>
                  <div className="text-xs text-slate-400">{c.patient_pn || ""}</div>
                </td>
                <td className="px-3 py-1.5">{c.case_number || "—"}</td>
                <td className="px-3 py-1.5">{c.claim_number || "—"}</td>
                <td className="px-3 py-1.5">{usDate(c.date_of_service)}</td>
                <td className="px-3 py-1.5">{usDate(c.entered)}</td>
                <td className="px-3 py-1.5 text-right">{c.days}</td>
                <td className="px-3 py-1.5 text-right">{money(c.billed)}</td>
                <td className="px-3 py-1.5 text-right">{money(c.paid)}</td>
                <td className="px-3 py-1.5 text-right font-medium">{money(c.balance)}</td>
                <td className="px-3 py-1.5">{usDate(c.last_payment)}</td>
                <td className="px-3 py-1.5 text-xs">
                  {c.due ? <span className="rounded bg-rose-50 px-2 py-0.5 text-rose-800">due</span>
                    : c.never_paid ? <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-700">not paid yet</span>
                      : <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-800">paid short</span>}
                  {c.past_lien_limit && <span className="ml-1 rounded bg-slate-200 px-2 py-0.5 text-slate-800"
                                              data-testid="wcf-past-18-months">over 18 months</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-3 text-sm">
        <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}
                className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40">Previous</button>
        <span className="text-slate-500">
          {data.total === 0 ? "0" : `${offset + 1}-${Math.min(offset + PAGE, data.total)}`} of {data.total.toLocaleString()}
        </span>
        <button disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}
                className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40">Next</button>
      </div>
      <p className="mt-3 text-xs text-slate-500">Due first, then the oldest entry. Open a patient to see the case's charges, payments and EOB lines.</p>
    </>
  );
}

export default function BillingWCFollowup() {
  const { user } = useAuth();
  const { payorId } = useParams();
  const location = useLocation();
  const [allowed, setAllowed] = useState(null);
  const win = readWindow(location.search);
  const noCarrier = location.pathname.startsWith("/billing/wc/followup/no-carrier");

  useEffect(() => {
    let live = true;
    fetchBillingAccess(api, user?.id).then((ok) => {
      if (live) setAllowed(ok);
    });
    return () => {
      live = false;
    };
  }, [user?.id]);

  if (allowed === null) {
    return <div className="flex h-full items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!allowed) return <Navigate to="/" replace />;
  const top = !payorId && !noCarrier;
  return (
    <div className="p-8 max-w-7xl" data-testid="billing-wc-followup">
      <BillingNav current="wc-followup" />
      <div className="text-sm text-slate-500">
        <Link to="/billing" className="text-sky-700 hover:underline">Billing</Link>
        {" / "}
        {top ? "WC follow-up" : <Link to={{ pathname: "/billing/wc/followup", search: windowSearch(win) }} className="text-sky-700 hover:underline">WC follow-up</Link>}
      </div>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-900">WC follow-up by carrier</h1>
        <WindowToggle win={win} base={location.pathname} />
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Shadow mode. Read from the Raintree mirror; nothing here changes Raintree or goes to a carrier.
      </p>
      {top ? <Summary win={win} /> : <CarrierClaims payorId={noCarrier ? null : payorId} win={win} />}
    </div>
  );
}
